import { useState } from "react";
import { Link } from "react-router-dom";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ShieldAlert, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { ANAF_DISCLAIMER_VERSION } from "@/lib/legalVersions";
import { useAnafDisclaimerStatus } from "@/hooks/useAnafDisclaimerStatus";

interface Props {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Called only after the acknowledgment is logged server-side with IP. */
  onAcknowledged: () => void;
}

/**
 * One-time modal shown before the user's first e-Factura submission (per
 * ANAF_DISCLAIMER_VERSION). Mandatory checkbox → server-side log entry in
 * terms_acceptance_log with IP captured from x-forwarded-for — explicit
 * legal proof for Terms §9.1.
 */
const AnafDisclaimerDialog = ({ open, onOpenChange, onAcknowledged }: Props) => {
  const [checked, setChecked] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { markAcked } = useAnafDisclaimerStatus();

  const handleConfirm = async () => {
    if (!checked || submitting) return;
    setSubmitting(true);
    try {
      const { error } = await supabase.functions.invoke(
        "log-terms-acceptance",
        {
          body: {
            document_type: "anaf_disclaimer",
            version: ANAF_DISCLAIMER_VERSION,
          },
        },
      );
      if (error) throw error;
      markAcked();
      toast.success("Mulțumim. Poți continua.");
      setChecked(false);
      onOpenChange(false);
      onAcknowledged();
    } catch (err: any) {
      toast.error(
        err?.message || "Nu am putut înregistra confirmarea. Încearcă din nou.",
      );
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(o) => {
        if (submitting) return;
        if (!o) setChecked(false);
        onOpenChange(o);
      }}
    >
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <ShieldAlert className="h-5 w-5 text-primary" />
            Înainte de prima trimitere la ANAF
          </DialogTitle>
          <DialogDescription className="text-left pt-1">
            EZ Factura este o unealtă tehnică pentru transmiterea facturilor
            în formatul RO_CIUS către SPV. Nu suntem consultant fiscal sau
            reprezentant ANAF.{" "}
            <Link
              to="/terms#anaf-disclaimer"
              target="_blank"
              className="text-primary underline underline-offset-2"
            >
              Detalii
            </Link>
          </DialogDescription>
        </DialogHeader>

        <label
          htmlFor="anaf-ack"
          className="flex items-start gap-3 cursor-pointer rounded-lg border border-border bg-muted/30 p-3 hover:bg-muted/50 transition-colors"
        >
          <Checkbox
            id="anaf-ack"
            checked={checked}
            onCheckedChange={(v) => setChecked(v === true)}
            className="mt-0.5"
            disabled={submitting}
          />
          <span className="text-sm text-foreground leading-snug">
            Înțeleg că răspunderea pentru acuratețea facturilor (sume, TVA,
            CAEN, încadrare fiscală) îmi aparține integral.
          </span>
        </label>

        <DialogFooter className="gap-2 sm:gap-0">
          <Button onClick={handleConfirm} disabled={!checked || submitting} className="w-full sm:w-auto">
            {submitting ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin mr-2" />
                Se înregistrează...
              </>
            ) : (
              "Confirm și continui"
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default AnafDisclaimerDialog;
